import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useState,
} from "react";

import { getDemoLanguage } from "./demoLanguage.ts";

// SHA-256 of the demo password. Computed at design time so the plaintext
// never lives in the bundle. Note: this is a soft gate — a determined user
// could still inspect the bundle, bypass via devtools, or skip via the
// localStorage flag. It exists to keep casual visitors out of the public
// demo URL, not to provide real authentication.
const PASSWORD_HASH =
  "cde09def53eb492f20d0f353d7bb6b4dff80c3e2979309742f679afa3ad845cd";
const STORAGE_KEY = "fidoo-demo-unlocked";

// The gate renders before i18next boots, so its copy lives here. Czech is the
// demo default; a visitor who switched the app to English sees English.
const GATE_TEXT = {
  cs: {
    title: "Fidoo demo",
    subtitle: "Zadejte přístupový kód a prohlédněte si demo prostředí.",
    codeLabel: "Přístupový kód",
    error: "Nesprávný kód — zkuste to znovu.",
    unlocking: "Ověřuji…",
    submit: "Pokračovat",
    disclaimer:
      "Ukázkové prostředí s fiktivními daty. Není napojené na skutečnou bankovní infrastrukturu.",
    reset: "Obnovit výchozí stav dema",
  },
  en: {
    title: "Fidoo demo",
    subtitle: "Enter the access code to view the demo workspace.",
    codeLabel: "Access code",
    error: "Incorrect code — try again.",
    unlocking: "Unlocking…",
    submit: "Continue",
    disclaimer:
      "Showcase environment with fictional data. Not connected to real banking infrastructure.",
    reset: "Reset demo state",
  },
} as const;

function gateText() {
  return getDemoLanguage().startsWith("en") ? GATE_TEXT.en : GATE_TEXT.cs;
}

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function readUnlocked(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistUnlocked() {
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // Private mode or storage disabled — user will need to re-enter on reload.
  }
}

type Props = {
  children: ReactNode;
};

export function DemoPasswordGate({ children }: Props) {
  const text = gateText();
  const [unlocked, setUnlocked] = useState(() => readUnlocked());
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(false), 1800);
    return () => clearTimeout(t);
  }, [error]);

  if (unlocked) return <>{children}</>;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    const hash = await sha256Hex(value);
    if (hash === PASSWORD_HASH) {
      persistUnlocked();
      setUnlocked(true);
    } else {
      setError(true);
      setValue("");
    }
    setPending(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
        background:
          "radial-gradient(circle at 30% 20%, #fafbef 0%, #eef0d8 55%, #d9dba8 100%)",
        fontFamily:
          "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif",
        padding: "24px",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#ffffff",
          borderRadius: 16,
          padding: "40px 32px",
          boxShadow:
            "0 18px 60px -20px rgba(7, 39, 26, 0.32), 0 4px 12px -2px rgba(7, 39, 26, 0.12)",
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: "linear-gradient(135deg, #b9c925 0%, #00674a 100%)",
            display: "grid",
            placeItems: "center",
            marginBottom: 24,
            color: "#ffffff",
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: 0.3,
          }}
        >
          ✦
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 700,
            color: "#07271a",
            letterSpacing: -0.2,
          }}
        >
          {text.title}
        </h1>
        <p
          style={{
            marginTop: 6,
            marginBottom: 24,
            fontSize: 14,
            lineHeight: 1.5,
            color: "#475c50",
          }}
        >
          {text.subtitle}
        </p>
        <input
          type="password"
          autoFocus
          aria-label={text.codeLabel}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={text.codeLabel}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "14px 16px",
            fontSize: 15,
            border: `1.5px solid ${error ? "#dc2626" : "#d3d8c2"}`,
            borderRadius: 10,
            outline: "none",
            transition: "border-color 0.15s ease",
            color: "#07271a",
          }}
        />
        {error && (
          <div
            role="alert"
            style={{ marginTop: 10, fontSize: 13, color: "#dc2626" }}
          >
            {text.error}
          </div>
        )}
        <button
          type="submit"
          disabled={pending || value.length === 0}
          style={{
            marginTop: 20,
            width: "100%",
            padding: "13px 16px",
            fontSize: 15,
            fontWeight: 600,
            border: 0,
            borderRadius: 10,
            color: "#ffffff",
            background:
              value.length === 0 || pending
                ? "#a3ab84"
                : "linear-gradient(135deg, #00674a 0%, #014e36 100%)",
            cursor: value.length === 0 || pending ? "default" : "pointer",
            transition: "background 0.15s ease",
          }}
        >
          {pending ? text.unlocking : text.submit}
        </button>
        <p
          style={{
            marginTop: 24,
            marginBottom: 0,
            fontSize: 12,
            color: "#7e8b80",
          }}
        >
          {text.disclaimer}
        </p>
        <button
          type="button"
          onClick={() => {
            try {
              window.localStorage.clear();
              window.sessionStorage.clear();
            } catch {
              // Private mode — best effort.
            }
            window.location.replace(window.location.pathname);
          }}
          style={{
            marginTop: 10,
            background: "transparent",
            border: 0,
            padding: 0,
            color: "#7e8b80",
            fontSize: 11,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {text.reset}
        </button>
      </form>
    </div>
  );
}
